import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listCashEquivalentLedgers, getCashBook } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `general-accounts-report-cash-book`
 * ("Financial Cash Book") — for each chosen cash/bank ledger, a T-account
 * style rollup of every counter-ledger it moved money with in a date range.
 * Confirmed via `GeneralAccountReportService.cashBook` that the old app does
 * this entirely in Java (no native SQL) over the same entities this app
 * already has — new `GET /api/accounts/vouchers/reports/cash-book` mirrors
 * that in-Java grouping.
 */
export default function CashBookPage() {
  const [cashLedgers, setCashLedgers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listCashEquivalentLedgers().then((list) => {
      setCashLedgers(list);
      setSelectedIds(list.map((l) => String(l.ledgerId)));
    });
  }, []);

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    if (selectedIds.length === 0) {
      setError("Please select at least one cash ledger.");
      return;
    }
    setLoading(true);
    try {
      setData(await getCashBook(fromDate, toDate, selectedIds));
    } catch (err) {
      setError("Failed to load cash book.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Financial Cash Book</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="form-row">
        <span className="admit-card-label">Cash/Bank Ledgers:</span>{" "}
        {cashLedgers.map((l) => (
          <label key={l.ledgerId} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={selectedIds.includes(String(l.ledgerId))} onChange={() => toggle(String(l.ledgerId))} />
            {l.ledgerName}
          </label>
        ))}
      </div>
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        data && (
          <div>
            {data.details.map((group) => (
              <div key={group.ledgerId} className="table-scroll" style={{ marginBottom: 24 }}>
                <h3>{group.ledgerName}</h3>
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Counter Ledger</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.insideDetails.map((r) => (
                      <tr key={r.ledgerId}>
                        <td>{r.ledgerName}</td>
                        <td>{r.debitAmount.toFixed(2)}</td>
                        <td>{r.creditAmount.toFixed(2)}</td>
                      </tr>
                    ))}
                    {group.insideDetails.length === 0 && (
                      <tr>
                        <td colSpan={3}>No activity for this ledger in this period.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
            <p style={{ fontWeight: 800 }}>
              Grand Total — Debit: {data.totalDebit.toFixed(2)} / Credit: {data.totalCredit.toFixed(2)}
            </p>
          </div>
        )
      )}
    </MainLayout>
  );
}
